// "Navigating Firmark" — the shell, the palette, and the calculation sheet
// chrome. Every claim here describes shipped behavior; when the shell changes,
// this article changes in the same PR.
import type { HelpArticle } from "../../types";

export const navigatingFirmarkArticle: HelpArticle = {
  id: "navigating-firmark",
  title: "Navigating Firmark",
  description: "One shell for the whole product: the sidebar, the command palette, go-to shortcuts, and the calculation sheet header.",
  tags: ["shell", "navigation", "start here"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "Firmark runs inside one persistent shell: a spruce top bar, a single sidebar slot, and the working surface. Routes change what fills the slot and the surface; the shell itself never swaps out from under you.",
    },

    { kind: "heading", level: 2, text: "The sidebar", id: "sidebar" },
    {
      kind: "paragraph",
      text: "The portal sidebar groups every destination under three labels: **Navigation** (Dashboard), **Manage** (Projects, Calculations, Policies, Materials, Output & Docs), and **System** (Settings, Help). The collapse toggle in the top bar shrinks it to icons; that choice is remembered per browser.",
    },
    {
      kind: "paragraph",
      text: "Inside a calculation project the same slot shows that project's navigation instead — overview, member schedule, and each sheet. The portal nav is still one click away: the top-bar menu button opens it as an overlay drawer.",
    },
    {
      kind: "shot",
      shotId: "shell-sidebar",
      caption: "The shell on the Help page: spruce top bar, portal sidebar, working surface.",
    },

    { kind: "heading", level: 2, text: "The command palette", id: "command-palette" },
    {
      kind: "paragraph",
      text: "Press `Ctrl`+`K` (or `/`) anywhere to open the palette. It reaches every sidebar destination plus direct actions and your project list, so anything the sidebar can do, the palette can do without the pointer. Arrow keys move the highlight; `Enter` runs it; `Esc` closes.",
    },
    { kind: "shot", shotId: "command-palette" },

    { kind: "heading", level: 2, text: "Go-to shortcuts", id: "go-to-shortcuts" },
    {
      kind: "paragraph",
      text: "The `g` sequences jump between sections: press `g`, release, then a second key — `g` `h` opens Help, `g` `c` opens Calculations. The full inventory lives in [Keyboard shortcuts](/help/reference/keyboard-shortcuts), or press `?` for the same list in a dialog.",
    },

    { kind: "heading", level: 2, text: "Inside a calculation sheet", id: "calc-sheet" },
    {
      kind: "where",
      path: ["Calculations", "your project", "member sheet"],
      note: "Every sheet in a calc project opens under the same header strip.",
    },
    {
      kind: "paragraph",
      text: "The strip carries the **mark** (the calculation's name), the master control that drives the form (member role on wood sheets, calculator on the others), per-check utilization chips straight from the engine result, and the governing status pill.",
    },
    {
      kind: "shot",
      shotId: "calc-sheet-headerstrip",
      caption: "A floor joist sheet's header at laptop width — the chips wrap onto their own row.",
    },
    {
      kind: "paragraph",
      text: "Two header toggles change how much the sheet shows without changing what it computes. **Quick / Detailed** sets view density: Quick collapses the Design Conditions card and hides the factor ledger and detail accordions; Detailed (the default) shows the full record. **Lock** puts the sheet in presentation mode — the input pane goes read-only until you unlock it. Lock is a view state for working over someone's shoulder, not an approval.",
    },
    {
      kind: "paragraph",
      text: "Field-level help on sheets comes from the calculator registry, the same schema the engine validates inputs against. Help articles read those strings live rather than restating them; the design-method field is a fair sample of the depth behind each `?` marker:",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "design_method" },
    {
      kind: "heading", level: 3, text: "Linked reactions", id: "linked-reactions",
    },
    {
      kind: "paragraph",
      text: "Sheets can source loads from each other: a member or connection takes another member's support reaction as its demand, and the engine re-reads that reaction at every recalculation, preserving its load types. When an upstream sheet changes, downstream sheets are flagged out of date and a banner offers **Recompute all** — nothing recomputes behind your back.",
    },

    { kind: "heading", level: 2, text: "The help panel", id: "help-panel" },
    {
      kind: "paragraph",
      text: "Help opens two ways: this full page (`g` `h`, or Help in the sidebar), and a side panel that opens over whatever you're doing — links into help from inside the product open the panel so you keep your place. The panel is addressable: `?help=navigating-firmark` in any URL opens this article there, and `Esc` closes it and returns focus to where you were.",
    },
    {
      kind: "values",
      caption: "The shell's addressable entry points.",
      rows: [
        { label: "This article, in the side panel", value: "?help=navigating-firmark" },
        { label: "Full help page", value: "/app/help" },
        { label: "Command palette", value: "Ctrl+K" },
        { label: "Go to Help", value: "g h" },
      ],
    },

    { kind: "related", topics: ["keyboard-shortcuts"] },
  ],
};
